import React, {
  useState,
  Fragment,
  useRef,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { noop } from 'lodash';
import Sticky from 'react-stickynode';
import { Waypoint } from 'react-waypoint';
import { useRouter } from 'next/router';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES, TRACK_PAGE_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import {
  DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT,
  DEFAULT_PAGINATED_TRIPS_LIST_LIMIT,
} from 'Views/account/queries/account.queries';
import { AnchorsList } from 'Components/frameworks/anchors-list/AnchorsList';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { Title } from 'Components/frameworks/title/Title';
import { Alert } from 'Components/widgets/alert/Alert';
import { BookingsCartContainer } from 'Components/widgets/cart/BookingsCartContainer';
import type { Props as StaysSectionProps } from 'Components/widgets/stays-section/StaysSection';
import { StaysSection } from 'Components/widgets/stays-section/StaysSection';
import { UpsellsServicesLayers } from 'Components/widgets/upsells-services-layers/UpsellsServicesLayers';
import { AccountView } from 'Views/account/AccountView';
import { makeViewProps } from 'Src/types/view-helpers';
import { bookingsReducer, initialBookingsState, mapToLoadedAction } from './StaysView.reducer';
import { fetchBookings, flattenBookings, handleUnauthenticated } from './StaysView.utils';
type Props = {
  bookings: {
    canceled: Array<BookingEntity>;
    future: Array<BookingEntity>;
    past: Array<BookingEntity>;
    hasMorePastTrips: boolean;
    hasMoreFutureTrips: boolean;
    hasMoreCanceledTrips: boolean;
  };
  hasNoBookings: boolean;
};
export const StaysView = ({ bookings, hasNoBookings }: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const { future = [] } = bookings;
  const { query: queryParams } = useRouter();
  const location = {
    queryParams,
  };
  const appContext = useAppContext();
  const layerRef = useRef();
  const [activeSection, setActiveSection] = useState(null);
  const [pastBookings, dispatchPastBookings] = useReducer(bookingsReducer, initialBookingsState);
  const [futureBookings, dispatchFutureBookings] = useReducer(
    bookingsReducer,
    initialBookingsState,
  );
  const [canceledBookings, dispatchCanceledBookings] = useReducer(
    bookingsReducer,
    initialBookingsState,
  );
  useEffect(() => {
    dispatchPastBookings({
      type: 'SET_BOOKINGS',
      payload: {
        data: bookings.past,
        hasMoreTrips: bookings.hasMorePastTrips,
      },
    });
    dispatchFutureBookings({
      type: 'SET_BOOKINGS',
      payload: {
        data: bookings.future,
        hasMoreTrips: bookings.hasMoreFutureTrips,
      },
    });
    dispatchCanceledBookings({
      type: 'SET_BOOKINGS',
      payload: {
        data: bookings.canceled,
        hasMoreTrips: bookings.hasMoreCanceledTrips,
      },
    });
  }, [bookings]);
  const fetchMoreTrips = useCallback(
    (
      period: BookingsPeriods,
      limit: number,
      nextPage: number,
      isCanceled: boolean,
      shouldProposeUpsells: boolean,
    ): void => {
      const dispatch = {
        past: dispatchPastBookings,
        future: dispatchFutureBookings,
        all: noop,
        current: noop,
      }[period];
      dispatch({
        type: 'FETCH_NEXT_TRIPS_PAGE',
      });
      fetchBookings(
        appContext,
        {
          isCanceled,
          limit,
          page: nextPage,
          period,
        },
        {
          withUpsells: shouldProposeUpsells,
        },
      )
        .then(mapToLoadedAction)
        .then(dispatch)
        .catch(handleUnauthenticated(appContext, location));
    },
    [appContext, location],
  );
  const sections = useMemo(
    () =>
      makeViewProps<
        Array<
          StaysSectionProps & {
            bookings: unknown[];
            ctaDataTestId: string;
            trackingPayload: string;
          }
        >
      >(
        [
          {
            ...flattenBookings(futureBookings),
            enabled: !!futureBookings.data.length,
            fetchPeriod: 'future',
            id: 'future',
            isYearDisplayed: false,
            title: t('customerAccount.stays.currentFutureStaysListTitle'),
            trackingPayload: 'my_future_stays',
            ctaDataTestId: 'CTA-my-next-stays',
            dataTestId: 'Next-Stays-Block',
            limit: DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT,
            fetchMoreTrips,
            isCanceled: false,
            shouldProposeUpsells: true,
          },
          {
            ...flattenBookings(pastBookings),
            enabled: !!pastBookings.data.length,
            fetchPeriod: 'past',
            id: 'past',
            isYearDisplayed: true,
            title: t('customerAccount.stays.pastStaysListTitle'),
            trackingPayload: 'my_past_stays',
            ctaDataTestId: 'CTA-my-past-stays',
            dataTestId: 'Past-stays-Block',
            limit: DEFAULT_PAGINATED_TRIPS_LIST_LIMIT,
            fetchMoreTrips,
            isCanceled: false,
            shouldProposeUpsells: false,
          },
          {
            ...flattenBookings(canceledBookings),
            isYearDisplayed: true,
            id: 'canceled',
            fetchPeriod: 'all',
            title: t('customerAccount.stays.canceledStaysListTitle'),
            trackingPayload: 'my_cancelled_stays',
            ctaDataTestId: 'CTA-my-canceled-stays',
            dataTestId: 'Canceled-stays-Block',
            limit: DEFAULT_PAGINATED_TRIPS_LIST_LIMIT,
            fetchMoreTrips,
            isCanceled: true,
            shouldProposeUpsells: false,
          },
        ].filter((section) => !!(section as { bookings?: unknown[] }).bookings?.length),
      ),
    [pastBookings, futureBookings, canceledBookings, fetchMoreTrips],
  );

  const onEnterSection = (id) => {
    setActiveSection(id);
  };

  return (
    <Fragment>
      <TrackPage pageName={TRACK_PAGE_NAMES.CA_STAYS} />
      <AccountView
        breadcrumbItems={[
          {
            label: t('customerAccount.stays.staysListTitle'),
          },
        ]}
        meta={{
          title: t('customerAccount.stays.staysListTitle'),
        }}
      >
        <SiteContainer className="pb-20">
          <Title className="mb-20" hLevel={1} theme="title-brand-n0">
            {t('customerAccount.stays.staysListTitle')}
          </Title>
        </SiteContainer>

        {hasNoBookings ? (
          <SiteContainer className="pb-40">
            <Alert
              {...makeViewProps<any>({
                id: 'alert-no-stays',
                text: t('customerAccount.stays.noStaysLabel'),
              })}
            />
          </SiteContainer>
        ) : (
          <Fragment>
            {sections.length === 0 && (
              <div className="flex flex-col items-center justify-center py-60">
                <Spinner iconName={ICONS.TRIDENT1} />
              </div>
            )}

            {sections.length > 1 && (
              <Sticky innerZ={5}>
                <SiteContainer className="mb-20 bg-white py-20">
                  <nav
                    className="scroll-x scrollbar-hidden -mx-20 px-20 md:mx-0 md:px-0"
                    role="navigation"
                  >
                    <AnchorsList
                      data-testid="Breadcrumb-My-Stays"
                      items={sections.map((s) => ({
                        id: s.id,
                        isActive: activeSection === s.id,
                        'data-testid': s.ctaDataTestId,
                        label: s.title,
                        url: `#${s.id}`,
                        onClick: () =>
                          trackClickEvent(TRACK_EVENT_NAMES.CLICK_ANCHOR, {
                            detail_click: s.trackingPayload,
                          }),
                      }))}
                    />
                  </nav>
                </SiteContainer>
              </Sticky>
            )}

            {future.filter(Boolean).length > 0 && (
              <Fragment>
                <UpsellsServicesLayers bookings={future} forwardRef={layerRef} />
                <BookingsCartContainer bookings={future} forwardRef={layerRef} />
              </Fragment>
            )}
            {sections.map((section) => (
              <Fragment key={section.id}>
                <Waypoint onEnter={() => onEnterSection(section.id)} bottomOffset="90%" />
                <StaysSection {...section} forwardRef={layerRef} key={section.fetchPeriod} />
              </Fragment>
            ))}
          </Fragment>
        )}
      </AccountView>
    </Fragment>
  );
};
